/* Copyright (c) 2020-present CloudIO Inc */

import {
  AllDatasources,
  DBRow,
  FieldType,
  Query,
} from '@cloudio-saas/datasource-types';
import { atom } from '@cloudio-saas/statex';
import { Platform } from 'componentregistry';
import {
  componentJs,
  managerJs,
} from 'layout/Sidebar/panels/ComponentStoreView/ComponentStoreConstant';
import {
  DataSourceName,
  InsertManyFunction,
  InsertOneFunction,
  MultiPostFunction,
  SinglePostFunction,
} from '../TestsView/types';
import { SQLType } from 'platform/SQL';

export const CLOUDIO = 'cloudio';

export const ATTRS_DTS = `_id: string;
_rs: 'I' | 'U' | 'Q' | 'D' | 'N';`;

export function fieldTypeToTSType(fieldType?: FieldType): string {
  // Reference: 'Reference',
  // case 'Password':
  // case 'EncryptedString':
  // case 'Time':
  switch (fieldType) {
    case 'EncryptedString':
    case 'String':
    case 'Textarea':
    case 'UserID':
    case 'URL':
    case 'Phone':
    case 'Email':
    case 'Date':
    case 'DateTime':
      return 'string';
    case 'Double':
    case 'Integer':
    case 'Percent':
      return 'number';
    // case 'Currency':
    //   return '{code:string; amount: number;}';
    case 'Boolean':
      return 'boolean';
    // case 'StringArray':
    //   return 'string[]';
    // case 'IntegerArray':
    //   return 'int[]';
    // case 'Address':
    //   return `{
    //       line1: string;
    //       line2?: string;
    //       city: string;
    //       state: string;
    //       postalCode: string;
    //       county?: string;
    //       province?: string;
    //       country: string;
    //     }`;
    // case 'Location':
    //   return `{
    //         latitude: number;
    //         longitude: number;
    //       }`;
    // case 'Reference':
    //   return `{
    //             type: string;
    //             id: string;
    //           }`;
    case 'YN':
      return "'Y' | 'N'";
    default:
      return 'unknown';
  }
}

export interface Script {
  attribute:
    | 'Module'
    | 'ScheduledJob'
    | 'WebAPI'
    | 'afterDeleteScript'
    | 'afterInsertScript'
    | 'afterUpdateScript'
    | 'authApi'
    | 'beforeDeleteScript'
    | 'beforeInsertScript'
    | 'beforeUpdateScript'
    | 'componentJs'
    | 'describeApi'
    | 'managerJs'
    | 'pageController'
    | 'postApi'
    | 'postQueryScript'
    | 'preQueryScript'
    | 'queryApi'
    | 'TestScripts'
    | 'validateRowScript'
    | 'wf'
    | 'wfNode'
    | 'customTemplates';
  title: string;
  defaultScript: string;
  queryVars: string;
}

export const selectedTabAtom = atom<{
  index: number;
  attribute: Omit<
    Script['attribute'],
    'ScheduledJob' | 'WebAPI' | 'pageController' | 'wf'
  >;
}>({
  path: ['transient', 'dsSelectedTab'],
  defaultValue: { index: 0, attribute: 'preQueryScript' },
  urlParam: true,
});

export const selectedExtDsTabAtom = atom<{
  index: number;
  attribute: Omit<
    Script['attribute'],
    'ScheduledJob' | 'WebAPI' | 'Module' | 'pageController' | 'wf'
  >;
}>({
  path: ['transient', 'extdsSelectedTab'],
  defaultValue: { index: 0, attribute: 'authApi' },
  urlParam: true,
});

export const scripts: Script[] = [
  {
    attribute: 'preQueryScript',
    title: 'Pre Query',
    defaultScript: `
const {
  aggregateList,
  data, // key value pairs that will be eq/is filters
  fetchDistinct, // will be set to true to fetch distinct values
  filter, // e.g. [{ attrCode: {is: "value"} }]
  fullSQL,
  groupByAttributeCodeList, // 
  limit, // e.g. 20
  offset, // e.g. 0
  orderByClause, // e.g. "#attrCode# ASC"
  params, // key value pairs that are passed by the client for custom processing
  projection, // e.g. {"attrA": 1, "attrB": 1}
  selectAttributeCodeList, // e.g. ["attrA", "attrB"]
  sort, // e.g. { attrA: 1,  attrB: -1, attrC: 2, attrD: -3 }
  whereClause, // e.g. "#attrCode = ?"
  whereClauseParamList, // e.g. ["some bind value", 123] will have values if whereClause has bind variables
} = query;

/*
Pre Query script can be used to achieve the following
1. Validate incoming filters e.g. throw error when a mandatory filter is missing

e.g.

const mandatoryAttribute = '...';
const mandatoryFilter = query.filter.find(f => !!f[mandatoryAttribute]);
if (!mandatoryFilter && !data?.[mandatoryAttribute]) {
  throw new UserError(\`Mandatory filter \${mandatoryAttribute} is missing!. You must provide a value for this filter!\`);
}


2. Manipulate the above query object to add additional filters

e.g.

data['someAttr'] = 'Some Value';
or
query.filter.push({someAttr: {is: 'Some Value'}});

3. Return your own results skipping the default process

e.g.

skipQuery = true; // set skipQuery to true to inform the platform to skip the default query process
const external_rows = [{...}, {...}]; // fetch data on your own
rows = external_rows; // assign to the variable "rows"

*/
    `,
    queryVars: `declare global {
  let skipQuery: boolean;
  let rows: Row<A>[];
  const query: Query<A>;
}`,
  },
  {
    attribute: 'postQueryScript',
    title: 'Post Query',
    defaultScript: `// Post Query Script can be used to manipulate the results before
// sending them to the user (browser)

rows.forEach(row => {
  row['someAttr'] = 'value set in post query';
});`,
    queryVars: `declare global {
  const query: Query<A>;
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'beforeInsertScript',
    title: 'Before Insert',
    defaultScript: `// Use Before Insert Script to perform any validation or defaulting
// prior to inserting the given rows

rows.forEach(row => {
  // @ts-ignore someAttr
  if (row.someAttr === 'invalid value') {
    // @ts-ignore someAttr
    throw new UserError(\`Some Attribute has invalid value "\${row.someAttr}"!\`);
  }
});`,
    queryVars: `declare global {
  let skipDML: boolean;
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'afterInsertScript',
    title: 'After Insert',
    defaultScript: `// Use After Insert Script to perform manipulate
// the inserted rows before sending them to the user

rows.forEach(row => {
  // @ts-ignore someAttr
  row.someAttr === 'some value';
});`,
    queryVars: `declare global {
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'beforeUpdateScript',
    title: 'Before Update',
    defaultScript: `// Use Before Update Script to perform any validation or defaulting
// prior to updating the given rows

rows.forEach(row => {
  // @ts-ignore someAttr
  if (row.someAttr === 'invalid value') {
    // @ts-ignore someAttr
    throw new UserError(\`Some Attribute has invalid value "\${row.someAttr}"!\`);
  }
});`,
    queryVars: `declare global {
  let skipDML: boolean;
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'afterUpdateScript',
    title: 'After Update',
    defaultScript: `// Use After Update Script to perform manipulate
// the updated rows before sending them to the user

rows.forEach(row => {
  // @ts-ignore someAttr
  row.someAttr === 'some value';
});`,
    queryVars: `declare global {
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'beforeDeleteScript',
    title: 'Before Delete',
    defaultScript: `// Use Before Delete Script to perform any validation or defaulting
// prior to deleting the given rows

rows.forEach(row => {
  // @ts-ignore someAttr
  if (row.someAttr === 'invalid value') {
    // @ts-ignore someAttr
    throw new UserError(\`Some Attribute has invalid value "\${row.someAttr}"!\`);
  }
});`,
    queryVars: `declare global {
  let skipDML: boolean;
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'afterDeleteScript',
    title: 'After Delete',
    defaultScript: `// Use After Delete Script to perform any
// cleanup actions after deleting the rows

rows.forEach(row => {
  // delete dependent data for this parent row

  // @ts-ignore ChildDataSource
  const child_rows = db.find('ChildDataSource', { data: { parentId: row.id } }); // fetch child rows
  // @ts-ignore ChildDataSource
  db.deleteMany('ChildDataSource', child_rows); // delete child rows
});`,
    queryVars: `declare global {
  let rows: Row<A>[];
}`,
  },
  {
    attribute: 'validateRowScript',
    title: 'Validate Row',
    defaultScript: `// row - updated by the user
  `,
    queryVars: `declare global {
  let row: Row<A>;
}`,
  },
  {
    attribute: 'Module',
    title: 'Module',
    defaultScript: `// @ts-ignore lodash
import { camelCase } from "lodash";
// @ts-ignore my-other-module
import { someOtherReusableFunction } from "my-other-module"; // you can import from other modules like so

function someReusableFunction() {
  // some reusable code here
  // you can use db, cache & session global objects in this code
  return \`Hi \${camelCase(session.displayName())}!\`;
}

const SomeConstant = "xyz";

export { someReusableFunction, SomeConstant };

// you can then import these as shown below in other scripts

/* WebAPI Function

// @ts-ignore my-module
const { someReusableFunction, SomeConstant } = await import("my-module"); // Note: you must await on the import when imported outside a module
response.json({ SomeConstant, result: someReusableFunction() });

*/    
`,
    queryVars: `declare global {
}`,
  },
  {
    attribute: 'WebAPI',
    title: 'Cloud Function Web API',
    defaultScript: `// read parameter values from request body
const { paramA } = request.body;

// @ts-ignore lodash
const { camelCase } = await import("lodash");

// invoke 3rd party REST API
const resp = await db.fetch('https://...', { method: "POST", headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ paramA }) });
const json = resp.json();

// repond with a JSON object
response.json({ outputA: camelCase('some string'), outputB: 123, ...json });
`,
    queryVars: `declare global {
  const response: FunctionResponse;
  const request: FunctionRequest;
}`,
  },
  {
    attribute: 'ScheduledJob',
    title: 'Cloud Function',
    defaultScript: '',
    queryVars: `declare global {
  const testRun: boolean;
  const input: Record<string, any>;
}`,
  },
  {
    attribute: 'pageController',
    title: 'Page Controller',
    defaultScript: '',
    queryVars: `declare global {
}`,
  },
  {
    attribute: 'wf',
    title: 'Workflow Expression',
    defaultScript: '',
    queryVars: `declare global {
      const wf: WF;
      const input: Record<string, any>;
      const inputState: Record<string, any>;
      const inputParams: Record<string, any>;
    }`,
  },
  {
    attribute: 'wfNode',
    title: 'Workflow Node Code',
    defaultScript: '',
    queryVars: `declare global {
      const wf: WF;
      const input: Record<string, any>;
      const inputState: Record<string, any>;
    }`,
  },
  {
    attribute: 'authApi',
    title: 'Init API',
    defaultScript: `// This code will be invoked before calling describe, query & post APIs.
// You can use this to set/initialize value required to invoke the external REST API e.g. auth tokens etc.

initData.AUTH_CODE = "some.code...";

// You can then access the above initData object in describe, query & post APIs
`,
    queryVars: `declare global {
  let initData: Record<string, any>;
  const ds: string;
  const objectName: string;
}`,
  },
  {
    attribute: 'describeApi',
    title: 'Describe API',
    defaultScript: `/*
This code will be invoked during datasource creation & re-sync
You must populate attrs with any array of attributes of type ExtDataSourceAttribute {
  allowBlank?: YN;
  attributeCode: string;
  attributeName: string;
  attributeType: FieldType;
  auditEnabled?: YN;
  autoSequence?: YN;
  columnName?: string;
  defaultValue?: string;
  exportAllowed?: YN;
  insertAllowed?: YN;
  maxLength?: number;
  outParam?: YN;
  paramName?: string;
  primaryKey?: YN;
  queryAllowed?: YN;
  scale?: number;
  selectAllowed?: YN;
  updateAllowed?: YN;
};
*/

function fieldTypeToAttributeType(fieldType) {
  // implement the type conversion
  switch (fieldType) {
    case 'Int':
      return 'Integer';
    case 'Timestamp':
      return 'DateTime';
    ...
    default:
      return 'String';
  }
}

function fieldsToAttributes(fields) {
  const transformedFields = fields.map(field => ({
    attributeCode: field.code,
    attributeName: field.label,
    attributeType: fieldTypeToAttributeType(field.type),
  }));
  return transformedFields;
}

const { token, instanceUrl } = initData;
const response = await db.fetch(\`https://changeme.com/api/describe/\${ds}\`, {
    method: "GET",
    headers: {
        Authorization: \`Bearer \${token}\`
    },
});
const json = response.json();
const transformedFields = fieldsToAttributes(json.fields);
attrs.push(...transformedFields);

// or hardcoded attributes
attrs.push({ attributeCode: "id", attributeName: "ID", attributeType: "Integer", primaryKey: "Y" }); // Primary Key
attrs.push({ attributeCode: "firstName", attributeName: "First Name", attributeType: "String" });
attrs.push({ columnName: "modTs", attributeCode: "lastUpdateDate", attributeName: "Last Update Date", attributeType: "DateTime" }); // WHO Column
attrs.push({ columnName: "modBy", attributeCode: "lastUpdatedBy", attributeName: "Last Updated By", attributeType: "String" }); // WHO Column
attrs.push({ columnName: "insTs", attributeCode: "creationDate", attributeName: "Creation Date", attributeType: "DateTime" }); // WHO Column
attrs.push({ columnName: "insBy", attributeCode: "createdBy", attributeName: "Created By", attributeType: "String" }); // WHO Column

// IMPORTANT: Make sure to set primaryKey to "Y" for atleast one attribute

// IMPORTANT: Make sure to include lastUpdateDate & lastUpdatedBy attribute codes
// if the data is editable

// The datasource created using this external datasource will be populated with the 
// above attributes
`,
    queryVars: `declare global {
  const initData: Record<string, any>;
  const ds: string;
  const objectName: string;
  const attrs: ExtDataSourceAttribute[];
}`,
  },
  {
    attribute: 'queryApi',
    title: 'Query API',
    defaultScript: `// Use the incoming \`query\` to transform into filters to be 
// passed to the external REST API and set \`rows\` variable with the
// resulting data list

// e.g. calling an external API
const transformedFilters = {}; // transform incoming query according to the external API spec
const { token, instanceUrl } = initData;
const response = await db.fetch(\`https://changeme.com/api/get/\${ds}\`, {
    method: "POST",
    headers: {
        Authorization: \`Bearer \${token}\`
    },
    body: JSON.stringify({ filters: transformedFilters }) // restructure the filters according to the external API spec
});
const json = response.json();
rows.push(...json.data);

// e.g. returning hardcoded data
const external_rows = [
    { firstName: "Larry", id: 1, lastUpdateDate: '2022-01-11T08:51:45.953Z', lastUpdatedBy: 'admin' },
    { firstName: "Steve", id: 2, lastUpdateDate: '2022-01-11T08:51:45.953Z', lastUpdatedBy: 'admin' }
];
rows.push(...external_rows);`,
    queryVars: `declare global {
  let rows: Row<A>[];
  const query: Query<A>;
  const initData: Record<string, any>;
  const ds: string;
  const objectName: string;
  const attrs: ExtDataSourceAttribute[];
  let queryElapsed: number;
  let pagination: Record<string, string> | undefined;
  let connectionElapsed: number;
  let sql: string;
  let params: unknown[];
  let rowsFetched: number;   
}`,
  },
  {
    attribute: 'postApi',
    title: 'Post API',
    defaultScript: `// Use the incoming \`rows\` to post to the external REST API

// IMPORTANT: You must return the rows with updated data if any in the same order in which it's passed
// Do not add or replace the elements inside rows. Mutate it instead.

const { token, instanceUrl } = initData;

// manipulate the data according to the external API spec
const rowsToPost = rows.map((row) => {
    row['modTs'] = row.lastUpdateDate;
    delete row.lastUpdateDate;
    row['modBy'] = row.lastUpdatedBy;
    delete row.lastUpdatedBy;
    row['insTs'] = row.creationDate;
    delete row.creationDate;
    row['insBy'] = row.createdBy;
    delete row.createdBy;
});

const response = await db.fetch(\`https://changeme.com/api/post/\${ds}\`, {
    method: "POST",
    headers: {
        Authorization: \`Bearer \${token}\`
    },
    body: JSON.stringify({ data: rowsToPost }) // restructure the data according to the external API spec
});
const json = response.json();
const updatedRows = json.data; // get the updated rows either from the response or by re-fetching

rows.forEach((row, index) => {
    row.id = updatedRows[index].id; // update primary key or any values populated/updated by the external API
    row.lastUpdateDate = updatedRows[index].modTs; // update WHO values
    row.lastUpdatedBy = updatedRows[index].modBy; // update WHO values
    row.creationDate = updatedRows[index].insTs; // update WHO values
    row.createdBy = updatedRows[index].insBy; // update WHO values
});`,
    queryVars: `declare global {
  const rows: Row<A>[];
  const initData: Record<string, any>;
  const ds: string;
  const objectName: string;
  const attrs: ExtDataSourceAttribute[];
}`,
  },
  {
    attribute: 'managerJs',
    title: 'Manager',
    defaultScript: managerJs,
    queryVars: '',
  },
  {
    attribute: 'componentJs',
    title: 'Component',
    defaultScript: componentJs,
    queryVars: '',
  },
  {
    attribute: 'TestScripts',
    title: 'Test Scripts',
    defaultScript: '',
    queryVars: '',
  },
];

export const testScriptTypests = `
    import { WSRequest, WSSuccessResponse, AllDatasources, Query, DBRow } from '@cloudio-saas/datasource-types';
    import { Platform, UIDriver, MultiPostFunction, SinglePostFunction, TestFn, FnWithResult, FnWithResults, TestResult, SQLResult } from 'cloudio';
    type DataSourceName = keyof AllDatasources;
    declare global {
      const platform: Platform;    
      const ui: UIDriver;
      const expect: Chai.ExpectStatic;
      declare function test(name: string, fn: TestFn): void;
      declare function beforeAll(fn: TestFn): void;
      declare function afterAll(fn: FnWithResults): void;
      declare function beforeEach(fn: TestFn): void;
      declare function afterEach(fn: FnWithResult): void;
      declare function post(request: WSRequest, appUid: string): Promise<WSSuccessResponse<any>>;
      declare function find<T extends DataSourceName>(ds: T, request: Query<AllDatasources[T]>): Promise<DBRow<AllDatasources[T]>[]>;      
      const insertMany = MultiPostFunction;
      const updateMany = MultiPostFunction;
      const deleteMany = MultiPostFunction;
      const insertOne = SinglePostFunction;
      const updateOne = SinglePostFunction;
      const deleteOne = SinglePostFunction;
      declare function executeQuery(sql: string): Promise<SQLType>;
      declare function executeUpdate(sql: string): Promise<SQLType>;
    }
`;

export const uiScriptTypests = `
    import { Platform } from 'cloudio';
    declare global {
      const platform: Platform;    
      const appUid: string;
    }
`;

export interface RunUIScriptProps {
  platform: Platform;
  find: <T extends DataSourceName>(
    ds: T,
    request: Query<AllDatasources[T]>,
  ) => Promise<DBRow<AllDatasources[T]>[]>;
  insertMany: InsertManyFunction;
  updateMany: MultiPostFunction;
  deleteMany: MultiPostFunction;
  insertOne: InsertOneFunction;
  updateOne: SinglePostFunction;
  deleteOne: SinglePostFunction;
  executeQuery: (sql: string) => Promise<SQLType>;
  executeUpdate: (sql: string) => Promise<SQLType>;
  appUid: string;
  input?: Record<string, unknown>;
}
